import type { Locale } from "@/kernel/locale";

// Serializable contract of a connect-form request and of the stored lead
// (DSH-015). Stable codes, never translated labels: the UI translates them.

export const LEAD_INTERESTS = ["webChat", "messengers", "tablets", "videoOperator", "other"] as const;

export type LeadInterest = (typeof LEAD_INTERESTS)[number];

/** Fields the visitor must fill in; every other field is optional. */
export const LEAD_REQUIRED_FIELDS = ["company", "name", "contact"] as const;

export type LeadRequiredField = (typeof LEAD_REQUIRED_FIELDS)[number];

export const LEAD_TEXT_FIELDS = ["company", "sphere", "name", "role", "contact", "pointsCount", "comment"] as const;

export type LeadTextField = (typeof LEAD_TEXT_FIELDS)[number];

/** Longest accepted value of each field, in characters; the form sets the same maxlength. */
export const LEAD_LIMITS: Readonly<Record<LeadTextField, number>> = {
  company: 160,
  sphere: 120,
  name: 120,
  role: 120,
  contact: 160,
  pointsCount: 40,
  comment: 2000,
};

/** Largest request body the leads route reads. */
export const LEAD_BODY_MAX_BYTES = 16 * 1024;

export type LeadFieldError = "company_required" | "name_required" | "contact_invalid" | "too_long";

export type LeadFieldErrors = Partial<Record<LeadTextField, LeadFieldError>>;

export type LeadSubmission = {
  company: string;
  sphere: string;
  name: string;
  role: string;
  /** Work email or phone number. */
  contact: string;
  pointsCount: string;
  interests: LeadInterest[];
  comment: string;
  /** The language of the page the request was sent from. */
  locale: Locale;
  /** Honeypot: hidden from people, filled in by bots. Must stay empty. */
  website?: string;
};

export type LeadSubmissionResult =
  | { ok: true }
  | { ok: false; code: "invalid"; fields: LeadFieldErrors }
  | { ok: false; code: "rate_limited" }
  /** `reference` is the short support reference of the failed request. */
  | { ok: false; code: "unavailable"; reference?: string };

export type SubmitLead = (submission: LeadSubmission) => Promise<LeadSubmissionResult>;

export const LEAD_STATUSES = ["new", "in_progress", "done"] as const;

export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** A stored lead as the cabinet shows it. */
export type Lead = {
  id: string;
  /** ISO 8601. */
  createdAt: string;
  locale: Locale;
  company: string;
  name: string;
  role: string | null;
  contact: string;
  sphere: string | null;
  pointsCount: string | null;
  interests: LeadInterest[];
  comment: string | null;
  status: LeadStatus;
  statusChangedAt: string | null;
  /** When the Telegram notification was delivered; null when it was not. */
  notifiedAt: string | null;
};

// D-043 names (<product>.<area>.<entity>.<action>); the result goes to
// event.outcome and event.reason, never into the name.
export const LEAD_EVENTS = [
  { name: "iron.leads.lead.submit", category: "operation" },
  { name: "iron.leads.notification.send", category: "dependency" },
  { name: "iron.leads.lead.update_status", category: "operation" },
] as const;
